/**
 * W-040 — Moderation queue. Stories: US-A07.
 *
 * AC1 reports oldest first, with reason and count
 * AC6 a resolved report leaves the queue (only `status = 'open'` reports show)
 *
 * Reports have no foreign key to their target (`target_id` is polymorphic —
 * post or reply), so they cannot be embedded via a PostgREST join. Several
 * open reports commonly land on the same piece of content, so the queue
 * groups them by `(target_type, target_id)` client-side and links to the
 * oldest report in the group; `ReportShowPage` resolves the whole group at
 * once from there.
 */
import { List } from '@refinedev/antd'
import { useList } from '@refinedev/core'
import { Space, Table, Typography } from 'antd'
import dayjs from 'dayjs'
import { Link } from 'react-router'
import { StatusTag } from '@/components'
import { useAdminTranslate } from '@/providers/i18n-provider'
import type { Report } from '@/types'

interface ReportGroup {
  targetType: Report['target_type']
  targetId: string
  reason: string
  count: number
  oldestReportId: string
  oldestCreatedAt: string
}

function groupReports(reports: Report[]): ReportGroup[] {
  const byTarget = new Map<string, ReportGroup>()
  // Input is oldest-first, so the first report seen per target is the
  // group's oldest — nothing to compare on the way through.
  for (const report of reports) {
    const key = `${report.target_type}:${report.target_id}`
    const existing = byTarget.get(key)
    if (existing) {
      existing.count += 1
      continue
    }
    byTarget.set(key, {
      targetType: report.target_type,
      targetId: report.target_id,
      reason: report.reason,
      count: 1,
      oldestReportId: report.id,
      oldestCreatedAt: report.created_at,
    })
  }
  return [...byTarget.values()].sort(
    (a, b) => new Date(a.oldestCreatedAt).getTime() - new Date(b.oldestCreatedAt).getTime(),
  )
}

export const ModerationQueuePage = () => {
  const translate = useAdminTranslate()

  const { data, isLoading } = useList<Report>({
    resource: 'reports',
    filters: [{ field: 'status', operator: 'eq', value: 'open' }],
    sorters: [{ field: 'created_at', order: 'asc' }],
    pagination: { mode: 'off' },
  })

  const groups = groupReports(data?.data ?? [])

  return (
    <List
      title={translate('admin.pages.moderationQueue')}
      canCreate={false}
      headerProps={{
        className: 'admin-list-header',
        subTitle: translate('admin.screens.moderation.queueSubtitle'),
      }}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Table<ReportGroup>
          rowKey={row => `${row.targetType}:${row.targetId}`}
          dataSource={groups}
          loading={isLoading}
          pagination={false}
          locale={{ emptyText: translate('admin.screens.moderation.queueEmpty') }}
          columns={[
            {
              title: translate('admin.screens.moderation.reportedAt'),
              dataIndex: 'oldestCreatedAt',
              render: (value: string) => dayjs(value).format('DD/MM/YYYY HH:mm'),
            },
            {
              title: translate('admin.common.type'),
              dataIndex: 'targetType',
              render: (value: Report['target_type']) =>
                translate(`admin.screens.moderation.target${value === 'post' ? 'Post' : 'Reply'}`),
            },
            {
              title: translate('admin.screens.moderation.reportReason'),
              dataIndex: 'reason',
              ellipsis: true,
            },
            { title: translate('admin.screens.moderation.reportCount'), dataIndex: 'count' },
            {
              title: translate('admin.columns.status'),
              render: () => <StatusTag kind="report" value="open" />,
            },
            {
              title: translate('admin.columns.actions'),
              render: (_: unknown, row: ReportGroup) => (
                <Link to={`/moderation/${row.oldestReportId}`}>
                  {translate('admin.screens.moderation.view')}
                </Link>
              ),
            },
          ]}
        />

        <Typography.Text type="secondary">
          {translate('admin.screens.moderation.queueFooter')}
        </Typography.Text>
      </Space>
    </List>
  )
}
